import { useEffect, useState } from 'react'
import { Netzstreifen } from '../../components/Netzstreifen'
import { Link, useNavigate } from 'react-router-dom'

import { useHealth, useLogout, useMe } from '../../hooks/queries'
import { gemerkterOrt, startziel, type Startziel } from '../../lib/letzterChat'
import { useOnline, useOutboxAnzahl } from '../../hooks/useOutbox'
import { BackIcon } from '../../components/Icons'
import { bodenLage, hoehenVerlauf, sichtVerworfen } from '../../lib/tastatur'
import { sendeVerlauf } from '../../lib/sendeprotokoll'
import { ChatfarbenCard } from './ChatfarbenCard'
import { KnopffarbeCard } from './KnopffarbeCard'
import { DiensteCard } from './DiensteCard'
import { DarstellungCard } from './DarstellungCard'
import { ErkennungCard } from './ErkennungCard'
import { GifCard } from './GifCard'
import { LimitsCard } from './LimitsCard'
import { PushCard } from './PushCard'
import { SpeicherCard } from './SpeicherCard'

export function SettingsPage() {
  const me = useMe()
  const logout = useLogout()
  const navigate = useNavigate()

  return (
    <>
      <header className="bar">
        <Link className="icon-button" to="/" aria-label="Zurück">
          <BackIcon />
        </Link>
        <h2 className="bar__title bar__grow">Einstellungen</h2>
      </header>
      <Netzstreifen />

      <div className="scroll-panel">
        <div className="card card--seite">
          <h2 className="card__titel">{me.data?.display_name}</h2>
          <p className="card__lead">{me.data?.email}</p>

          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: -6 }}>
            {me.data?.may_manage_people
              ? 'Du darfst Reisen anlegen und Personen freischalten.'
              : me.data?.may_create_trips
                ? 'Du darfst eigene Reisen anlegen.'
                : 'Du liest mit und antwortest.'}
          </p>

          {me.data?.may_manage_people && (
            <Link
              className="button button--outline"
              to="/settings/people"
              style={{ marginBottom: 10 }}
            >
              Personen verwalten
            </Link>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={logout.isPending}
            onClick={() =>
              logout.mutate(undefined, {
                onSuccess: () => navigate('/login', { replace: true }),
              })
            }
          >
            Abmelden
          </button>
        </div>

        <PushCard />

        <DarstellungCard />

        <LimitsCard />

        {/* Direkt vor den Diensten: beides gehört zu dem, was aus einer Route
            von selbst entsteht -- erst der Aufenthalt, dann sein Name. */}
        <ChatfarbenCard />
        <KnopffarbeCard />

        <ErkennungCard />

        <DiensteCard />

        <GifCard />

        {/* Ganz unten bei den Dingen, die nur die Verwaltung sieht: es geht
            nicht um eine Einstellung, sondern um den Zustand des Pakets. */}
        <SpeicherCard />

        {isIos() && !isStandalone() && (
          <div className="card card--seite" style={{ marginBottom: 0 }}>
            <h2 className="card__titel">Auf dem iPhone installieren</h2>
            {/* Ohne diesen Schritt gibt es auf iOS keine Benachrichtigungen --
                Apple gibt die Push-Schnittstelle nur Web-Apps auf dem
                Home-Bildschirm, nicht Seiten im Safari-Tab. */}
            <p className="card__lead" style={{ marginBottom: 0 }}>
              Tippe in Safari unten auf <strong>Teilen</strong> und dann auf{' '}
              <strong>Zum Home-Bildschirm</strong>. Nur dann kann dich die App
              später über neue Nachrichten benachrichtigen.
            </p>
          </div>
        )}

        {/* Ganz unten, und das mit Absicht: die beiden Karten sind
            Nachschlagewerk für den Fehlerfall, nicht Bedienung. Wer hier
            etwas einstellen will, soll nicht erst an Messwerten vorbei. */}
        <Netzlage />

        <Tastaturlage />

        <Stand />
      </div>
    </>
  )
}

/**
 * Welcher Stand hier läuft -- und welcher auf dem Server.
 *
 * Zwei Angaben statt einer, weil genau ihr Auseinanderfallen der Fehler war,
 * den sonst niemand sieht: ein Browser, der eine alte Fassung aus dem
 * Zwischenspeicher fährt, sieht vollkommen gesund aus.
 *
 * Aus demselben Grund steht hier auch, **wo die Anwendung beim nächsten Mal
 * aufmacht**. Aus dem Betrieb: am iPad kam der verlassene Chat, am iPhone
 * immer irgendein Chat — und ob dort nichts gemerkt war oder etwas Falsches,
 * ließ sich von außen nicht sehen. Eine Zeile beendet das Raten; sie steht
 * neben den Ständen, weil beides zur selben Frage gehört, wenn zwei Geräte
 * sich verschieden verhalten.
 */
function Stand() {
  const health = useHealth(true)

  return (
    <p
      style={{
        margin: '18px auto 0',
        maxWidth: 420,
        fontSize: 12,
        textAlign: 'center',
        color: 'var(--text-muted)',
      }}
    >
      Travel Broadcast {health.data?.version ?? '—'} · Oberfläche {__BUILD__} ·
      Server {health.data?.build ?? '—'}
      <br />
      Anzeige: {anzeigeArt()} · {herkunft()} · beim Öffnen gelesen:{' '}
      {nenne(startziel())} · jetzt gemerkt: {nenne(gemerkterOrt())}
    </p>
  )
}

/**
 * Womit die Seite geladen wurde — und ob der Browser das als sicher ansieht.
 *
 * Aus dem Betrieb: in der Web-App meldet iOS „Die Verbindung zu dieser Website
 * ist nicht sicher" mit einem leeren Zertifikat, in Safari dagegen das gültige.
 * Von außen sind dafür zwei ganz verschiedene Ursachen möglich — die App läuft
 * tatsächlich über `http`, weil das Symbol einmal von einer unverschlüsselten
 * Adresse angelegt wurde, oder jenes Blatt sagt in einer Web-App schlicht
 * nichts Sinnvolles. Der Unterschied entscheidet, ob etwas zu reparieren ist,
 * und die Seite selbst weiß ihn.
 */
function herkunft(): string {
  return `${location.protocol}${window.isSecureContext ? '' : ' (unsicher)'}`
}

/** „Chat /t/7", „Übersicht" oder „nichts" -- in einer Zeile lesbar. */
function nenne(ziel: Startziel): string {
  if (ziel === null) return 'nichts'
  return ziel.art === 'liste' ? 'Übersicht' : `Chat ${ziel.pfad}`
}

/**
 * Was das Gerät über seine Verbindung denkt -- und was noch aussteht.
 *
 * Diese Zeile beendet ein Ratespiel. Aus dem Betrieb: „Flugmodus an, aber kein
 * Hinweis" — und in Wahrheit war das WLAN weiter an, denn der Flugmodus
 * schaltet es auf dem iPhone nicht zwingend ab. Das Flugzeugsymbol sagt eben
 * nichts darüber, ob etwas durchgeht.
 *
 * Deshalb stehen hier **drei Angaben nebeneinander**: was der Browser meldet
 * (`navigator.onLine`), was zuletzt tatsächlich passiert ist (`netzStand()`)
 * und wie viele Nachrichten warten. Fallen die ersten beiden auseinander,
 * hängt das Gerät an einem Netz, durch das nichts hindurchgeht.
 */
function Netzlage() {
  const online = useOnline()
  const wartend = useOutboxAnzahl()
  const [browser, setBrowser] = useState(() => navigator.onLine)

  useEffect(() => {
    const pruefen = () => setBrowser(navigator.onLine)
    window.addEventListener('online', pruefen)
    window.addEventListener('offline', pruefen)
    return () => {
      window.removeEventListener('online', pruefen)
      window.removeEventListener('offline', pruefen)
    }
  }, [])

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Verbindung</h2>
      <p className="card__lead" style={{ marginBottom: 8 }}>
        {online
          ? 'Es geht gerade etwas durch.'
          : 'Zurzeit kommt nichts durch — was du schreibst, wartet im Gerät.'}
      </p>

      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-muted)' }}>
        <li>Zuletzt tatsächlich erreicht: {online ? 'ja' : 'nein'}</li>
        <li>Der Browser meldet: {browser ? 'online' : 'offline'}</li>
        <li>
          Wartende Nachrichten: <strong>{wartend}</strong>
        </li>
      </ul>

      {browser && !online && (
        <p className="notice" role="alert" style={{ marginTop: 10 }}>
          Das Gerät hängt an einem Netz, durch das nichts hindurchgeht — etwa
          einem WLAN ohne Verbindung nach draußen. Das kommt auch im Flugmodus
          vor, wenn das WLAN dabei angeschaltet bleibt.
        </p>
      )}

      {/* Eine unverschlüsselte Verbindung fällt sonst nicht auf, richtet aber
          still Schaden an: ohne sie gibt es keine Benachrichtigungen, und was
          hier steht, liest jeder im selben Netz mit. Auf dem Telefon entsteht
          das meist so, dass das Symbol einmal von einer `http`-Adresse aus
          angelegt wurde -- die Web-App startet dann für immer dort. */}
      {!window.isSecureContext && (
        <p className="notice" role="alert" style={{ marginTop: 10 }}>
          Diese Seite läuft <strong>unverschlüsselt</strong> ({location.protocol}
          ). Benachrichtigungen sind damit nicht möglich, und mitlesen kann
          jeder im selben Netz. Ruf sie über <code>https://</code> auf — und
          wenn du sie auf dem Home-Bildschirm hast, lösche das Symbol und lege
          es von der sicheren Adresse aus neu an.
        </p>
      )}
    </div>
  )
}

/**
 * Was das Gerät tut, wenn die Tastatur aufgeht.
 *
 * Dieselbe Absicht wie bei *Verbindung* darüber: eine Frage beenden, die von
 * außen nicht zu beantworten ist. Ob die Eingabezeile unter der Tastatur
 * liegt, hängt an vier Zahlen, und keine davon ist zu sehen — der Prüfstand
 * hier hat keine iOS-Tastatur, und ein Bildschirmfoto allein sagt nicht, ob
 * die Fläche verschoben oder der Abstand falsch gemessen ist.
 *
 * Deshalb ein Feld zum Hineintippen und die Zahlen darunter, die sich dabei
 * bewegen. Fehlt der Abstand, während die Tastatur offen ist, misst der
 * Browser anders als angenommen; ist der Versatz groß, schiebt das Gerät die
 * Fläche.
 */
function Tastaturlage() {
  const [werte, setWerte] = useState(lies)
  /**
   * Einmal beim Einhängen, und nie wieder.
   *
   * **Ein Messgerät, das den Messwert stört, ist keins.** Beide Messungen
   * hängen einen `position: fixed`-Kasten ins Dokument und nehmen ihn wieder
   * heraus -- einer davon mit `height: 100dvh`. Sie standen in `lies()`, und
   * das läuft bei **jedem** Ereignis der sichtbaren Fläche, also durch die
   * ganze Tastaturanimation hindurch. Beide Werte ändern sich ohnehin nicht,
   * solange das Gerät nicht gedreht wird.
   */
  const [fest] = useState(() => ({ dvh: `${dvhMessen()}`, raender: raenderMessen() }))
  /**
   * Der Mitschrieb wird **einmal beim Einhängen** geholt und danach nicht mehr.
   *
   * Sonst schöbe jedes Ereignis dieser Seite die Einträge weg, um die es geht
   * -- die aus dem Chat, von *vor* dem Wechsel hierher.
   */
  const [verlauf] = useState(hoehenVerlauf)
  /** Derselbe Grund wie beim Mitschrieb der Höhen: einmal beim Einhängen. */
  const [senden] = useState(sendeVerlauf)

  useEffect(() => {
    const sicht = window.visualViewport
    const auffrischen = () => setWerte(lies())

    // **Einmal nach dem Einhängen.** `useState(lies)` läuft beim Zeichnen, und
    // da steht `.shell` noch nicht im Dokument -- die Zeile bliebe bei „—"
    // stehen, bis sich zufällig etwas bewegt.
    auffrischen()

    sicht?.addEventListener('resize', auffrischen)
    sicht?.addEventListener('scroll', auffrischen)
    window.addEventListener('resize', auffrischen)
    return () => {
      sicht?.removeEventListener('resize', auffrischen)
      sicht?.removeEventListener('scroll', auffrischen)
      window.removeEventListener('resize', auffrischen)
    }
  }, [])

  // Wo der Browser die Tastatur selbst wegrechnet, gibt es hier nichts zu
  // sehen und nichts zu erklären.
  if (!window.visualViewport) return null

  return (
    <div className="card card--seite">
      <h2 className="card__titel">Tastatur</h2>
      <p className="card__lead" style={{ marginBottom: 8 }}>
        Tippe ins Feld. Bleibt es über der Tastatur sichtbar, sitzt es richtig.
      </p>

      <input
        className="field__input"
        type="text"
        placeholder="Hier tippen"
        aria-label="Testfeld für die Tastatur"
        style={{ marginBottom: 10 }}
      />

      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--text-muted)' }}>
        <li>
          Höhe der Anwendung: <strong>{werte.shell}</strong> von {werte.fenster}
        </li>
        <li>
          Freigehaltener Abstand: <strong>{werte.abstand}</strong>
        </li>
        <li>Fensterhöhe: {werte.fenster}</li>
        <li>Sichtbare Höhe: {werte.sicht}</li>
        <li>Versatz der Fläche: {werte.versatz}</li>
        <li>Seitenversatz: {werte.seite}</li>
        <li>Gesetzte Höhe: {werte.gesetzt}</li>
        <li>100dvh ergibt: {fest.dvh}</li>
        <li>Bildschirm: {werte.schirm}</li>
        <li>Fensterbreite: {werte.breite}</li>
        <li>Als Web-App: {werte.alsApp}</li>
        <li>
          Mindesthöhe vom Bildschirm: <strong>{werte.boden}</strong>
        </li>
        <li>Sicherheitsabstände: {fest.raender}</li>
      </ul>

      {/* **Der Mitschrieb, und er ist der eigentliche Zweck dieser Karte.**
          Aus dem Betrieb kam ein Bildschirmfoto mit dem Streifen und eine
          Minute später diese Karte mit „874 von 874". Die Messung von `100dvh`
          hängt einen Kasten ins Dokument, erzwingt damit einen Neuaufbau und
          räumt den Zustand weg, den sie zeigen soll. Hier steht, was *vorher*
          war -- aufgeschrieben aus Werten, die nichts erzwingen. */}
      <p className="card__lead" style={{ margin: '12px 0 4px' }}>
        Mitschrieb — Fenster / sichtbar / Anwendung / Unterkante · Seite ↕
        Fläche, neueste zuerst:
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
        {verlauf.map((stand, i) => (
          <li key={i}>
            <strong>{stand.fenster}</strong> / {stand.sicht} / {
              stand.shell === null ? '—' : <strong>{stand.shell}</strong>
            }
            {' / '}{stand.zeile === null ? '—' : <strong>{stand.zeile}</strong>}
            {' · '}<strong>{stand.seite}</strong>↕<strong>{stand.versatz}</strong>
            {' · '}{stand.zeit} · {stand.anlass} · {stand.pfad}
          </li>
        ))}
      </ul>

      {/* **Der Sendeknopf, aufgeschrieben** (#28). Aus dem Betrieb: „Senden
          über den Sendeknopf: geht manchmal, manchmal nicht." Hier steht, ob
          der Druck überhaupt angekommen ist -- verschluckt iOS den Tipp, steht
          gar nichts da -- und wo `submit()` hängengeblieben ist. */}
      <p className="card__lead" style={{ margin: '12px 0 4px' }}>
        Sendeknopf — neueste zuerst:
      </p>
      {senden.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12, color: 'var(--text-muted)' }}>
          Noch nichts. Schreib im Chat etwas und tippe den Sendeknopf an, dann
          komm zurück.
        </p>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--text-muted)' }}>
          {senden.map((schritt, i) => (
            <li key={i}>
              {schritt.zeit} · {schritt.was}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Was `100dvh` auf diesem Gerät wirklich ergibt.
 *
 * **Ausgemessen und nicht erfragt.** Es gibt keine Schnittstelle, die eine
 * CSS-Einheit ausrechnet; also bekommt ein unsichtbarer Kasten die Einheit
 * und wird vermessen. Genau diese Zahl war der blinde Fleck: `dvh` stand für
 * das Sichtbare und war es auf zwei Geräten nicht.
 */
function dvhMessen(): number {
  const kasten = document.createElement('div')
  kasten.style.cssText
    = 'position:fixed;top:0;left:0;width:1px;height:100dvh;visibility:hidden;pointer-events:none'
  document.body.appendChild(kasten)
  const hoch = Math.round(kasten.getBoundingClientRect().height)
  kasten.remove()
  return hoch
}

/**
 * Die vier Sicherheitsabstände.
 *
 * Dasselbe Vorgehen: `env()` lässt sich nur über eine Eigenschaft lesen, die
 * eine Länge nimmt. `padding` an einem unsichtbaren Kasten tut das.
 */
function raenderMessen(): string {
  const kasten = document.createElement('div')
  kasten.style.cssText
    = 'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;'
    + 'padding:env(safe-area-inset-top) env(safe-area-inset-right)'
    + ' env(safe-area-inset-bottom) env(safe-area-inset-left)'
  document.body.appendChild(kasten)
  const stil = getComputedStyle(kasten)
  const raus = [stil.paddingTop, stil.paddingRight, stil.paddingBottom, stil.paddingLeft]
    .map((wert) => Math.round(parseFloat(wert)))
    .join(' / ')
  kasten.remove()
  return raus
}

function lies() {
  const sicht = window.visualViewport
  const shell = document.querySelector('.shell')
  return {
    fenster: Math.round(window.innerHeight),
    // **Der Rohwert bleibt stehen, der Befund kommt dazu.** Genau diese Zahl
    // hat den Müllwert −33554432 gefunden -- sie zu glätten hieße, das
    // Messgerät zu verstellen. Ob sie verrechnet wird, entscheidet die
    // Verwerfungsgrenze in `tastatur.ts`; hier steht nur, was sie entschied.
    sicht: sicht
      ? `${Math.round(sicht.height)}${sichtVerworfen() ? ' (verworfen)' : ''}`
      : '0',
    versatz: sicht ? Math.round(sicht.offsetTop) : 0,
    seite: Math.round(window.scrollY),
    abstand:
      getComputedStyle(document.documentElement).getPropertyValue('--tastatur-hoehe').trim() ||
      '0px',
    // **Die Zahlen, die beim letzten Mal gefehlt haben.** Der Streifen unter
    // der Eingabezeile war aus einem Bildschirmfoto zu rechnen, weil das Gerät
    // ihn nicht selbst nennen konnte. Jetzt nennt es ihn.
    shell: shell === null ? '—' : `${Math.round(shell.getBoundingClientRect().height)}`,
    gesetzt:
      getComputedStyle(document.documentElement).getPropertyValue('--app-hoehe').trim()
      || 'nicht gesetzt',
    schirm: `${Math.round(window.screen.width)} × ${Math.round(window.screen.height)}`,
    // **Die beiden Zahlen, an denen die Mindesthöhe hängt.** Ohne sie war aus der
    // Karte nicht zu sehen, *warum* er nicht griff -- und zwei Anläufe gingen
    // daran vorbei.
    ...(() => {
      const l = bodenLage()
      return {
        alsApp: l.alsApp ? 'ja' : 'nein',
        breite: `${l.breite}`,
        boden: l.boden === null ? 'greift nicht' : `${l.boden}`,
      }
    })(),
  }
}

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent)
}

/**
 * Läuft das hier als Web-App vom Home-Bildschirm?
 *
 * Auf iOS zählt `navigator.standalone` und sonst nichts. Der Anzeigemodus
 * allein reicht nicht: In-App-Browser können `display-mode: standalone`
 * melden, weil aus Sicht der Seite keine Leisten da sind — und dann bliebe
 * der Hinweis zum Installieren ausgerechnet dem verborgen, der ihn braucht.
 */
function isStandalone(): boolean {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  if (alsApp !== undefined) return alsApp
  return window.matchMedia('(display-mode: standalone)').matches
}

/**
 * Web-App oder Browser — und wenn Browser, ob eines mit Leisten.
 *
 * Aus dem Betrieb, und es hat eine Runde gekostet: ein Bildschirmfoto der
 * Standzeile kam aus einem In-App-Browser, während am Gerät sonst die Web-App
 * benutzt wird. Beides sieht innen gleich aus, hat unter iOS aber **je einen
 * eigenen Speicher** — was in dem einen gemerkt wurde, gibt es im anderen
 * nicht. Wer das nicht weiß, hält es für einen Fehler der Anwendung.
 *
 * `navigator.standalone` ist dabei die verlässlichere Auskunft: manche
 * In-App-Browser melden `display-mode: standalone` mit, weil aus Sicht der
 * Seite keine Leisten da sind. Fallen die beiden auseinander, steht genau das
 * da — geraten wird nicht.
 */
function anzeigeArt(): string {
  const alsApp = (navigator as { standalone?: boolean }).standalone
  const ohneLeisten = window.matchMedia('(display-mode: standalone)').matches

  if (alsApp === true) return 'App'
  if (alsApp === false) return ohneLeisten ? 'Browser (ohne Leisten)' : 'Browser'
  // Außerhalb von iOS gibt es das Feld nicht; dort genügt der Anzeigemodus.
  return ohneLeisten ? 'App' : 'Browser'
}
